/* eslint-disable no-param-reassign */
import { pick, isString, extend, isEmpty, find, get, map, clone, filter, pluck } from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { CampaignSubTypes } from '@bingads-webui-campaign-react/constants';
import { findTargetNodeAndAncestorsNodes } from '@bingads-webui-campaign-react/filter-options-group';

import {
  modelToViewModelForConversionGoals,
  bulkModelToViewModelForConversionGoals,
  modelToViewModelForLocationTargets,
  bulkModelToViewModelForLocationTargets,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import {
  modelToViewModelForDayTimeTargets,
  CRITERION_LEVEL,
} from '@bingads-webui-campaign-react/xandr-campaign';
import { modelToViewModelForCampaignLevelAudienceCriterions, modelToViewModelForTargetCriterions } from './campaign-criterions';

// TODO: Need to add shopping-related properties such as CPS
const whitelistedSchemaPropertyKeysForModel = [
  'Budget', 'Id', 'Name', 'Status', 'TimeZone', 'BidStrategyName', 'BudgetId',
  'DeliveryStatus', 'DeliveryStatusDetails', 'CampaignType', 'CampaignSubType',
  'IsBidLandscapeAvailable', 'AdScheduleUseSearcherTimeZone',
  'BiddingScheme',
  'CampaignSettings', 'DisplayBiddingStrategyType',
  'DisplayBiddingStrategyMaxCpc', 'DisplayBiddingStrategyTargetCpa', 'Languages',
  'BaseCampaignName',
  'VerifiedTrackingData',
  'CampaignProductScope', 'LocalInventoryAdsEnabled', 'ShoppableAdsEnabled',
  'Priority', 'ProviderId', 'FeedLabel', 'SalesCountry',
  'TrackingUrlTemplate', 'UrlCustomParameters', 'FinalUrlSuffix', 'FeedId',
  'PersonalizedOffersEnabled', 'CashbackPercent', 'CashbackScope',
];

const operatorOptions = {
  equal: '=',
  notequal: '≠',
};

export const viewModelToModelForConversionGoals = campaignViewModel => ({
  CampaignConversionGoal: campaignViewModel.data.conversionGoals && campaignViewModel.data.conversionGoals.originalConversionGoals ? campaignViewModel.data.conversionGoals.originalConversionGoals : [],
});

export const viewModelToModelForCampaign = campaignViewModel => ({
  ...pick(campaignViewModel.data, whitelistedSchemaPropertyKeysForModel),
});

export const bulkModelToViewModelForCampaign = ({
  campaignModel,
  originalCampaignModel,
  bundleCountries,
}) => {
  let data = {
    ...pick(campaignModel, [...whitelistedSchemaPropertyKeysForModel, 'ImportProperties']),
  };

  if (data.Id && isString(data.Id)) {
    // eslint-disable-next-line no-param-reassign
    data.Id = parseInt(data.Id, 10);
  }

  data = extend(data, bulkModelToViewModelForConversionGoals({
    campaignModel,
    originalCampaignViewModel: originalCampaignModel,
  }));

  data = extend(data, bulkModelToViewModelForLocationTargets({
    locationTargetsModel: campaignModel.CampaignCriterions,
    bundleCountries,
  }));

  const campaignViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };

  return campaignViewModel;
};

const readCampaign = (cid, aid, campaignId) =>
  edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .get({
      select: ['Budget', 'CampaignConversionGoal/Goals'],
    })
    .toPromise();

export const loadCampaign = (cid, aid, campaignId) =>
  readCampaign(cid, aid, campaignId);

const getStoreNameById = (providerId, feedId, productData, feedData) => {
  let storeName = '';
  if (feedId > 0 && feedData && feedData.length > 0) {
    const selectedFeed = find(feedData, item => item.Id === feedId.toString());
    storeName = selectedFeed ? selectedFeed.Name : '';
  } else if (providerId > 0 && productData && productData.ProductCollectionAccounts && productData.ProductCollectionAccounts.length > 0) {
    const selectedProduct = find(productData.ProductCollectionAccounts, item => item.Value === providerId.toString());
    storeName = selectedProduct ? selectedProduct.Text : '';
  }

  return storeName;
};

// This will set multi values onto seperate lines.
const multilineValueInputProcessor = (valueInput, isEnhancedPAFilterP1Enabled) => {
  // Value input will be val1;;val2;;val3;;etc.
  const valuesArr = valueInput.split(';;');

  if (!isEnhancedPAFilterP1Enabled || valuesArr.length === 1) {
    return valueInput;
  }
  let output = valuesArr[0];

  for (let i = 1; i < valuesArr.length; i++) {
    output = `${output}, ${valuesArr[i]}`;
  }
  return output;
};

// Reference: component-react/packages/filter-options-group/src/filter-options-group/index.js
const composeProductFilter = (productFilter) => {
  const {
    PresetOperator = 'equal', PresetValue, Text, ValueOptions,
  } = productFilter;
  const operator = operatorOptions[PresetOperator] || '=';

  if (ValueOptions === null) {
    return `${Text} ${operator} ${PresetValue}`;
  }

  const selectedItems = filter(ValueOptions, valueOption => valueOption.Selected);
  const selectedTexts = pluck(selectedItems, 'Text').join(', ');

  return `${Text} ${operator} ${selectedTexts}`;
};

const defaultSelectedDataSourceHelper = (dataSource, customerSelectedProductConditions, permissions, isDynamicDataFeedBasedCampaign) => {
  const customerSelectedProductConditionsCopy = map(customerSelectedProductConditions, clone);
  const defaultSelectedDataSource = [];

  // for each operand we get that has data.
  customerSelectedProductConditionsCopy.forEach((customerSelectedProductCondition) => {
    let selectedProductConditionItem;

    // First get the operand data that has a value.
    for (let i = 0; i < dataSource.length; i++) {
      const ds = dataSource[i];

      // We need to map the operands if this is migrated from legacy, as they may not match the values.
      if (!isDynamicDataFeedBasedCampaign && ds && ds.Value === customerSelectedProductCondition.Operand) {
        customerSelectedProductCondition.Operand = ds.Text;
      }
      const { target } = findTargetNodeAndAncestorsNodes(ds, customerSelectedProductCondition.Operand);

      if (target) {
        selectedProductConditionItem = target;
        break;
      }
    }

    // if we have found an operand with a value:
    if (selectedProductConditionItem) {
      const clonedItem = clone(selectedProductConditionItem);

      clonedItem.PresetOperator = customerSelectedProductCondition.Operator;
      // eslint-disable-next-line no-negated-condition
      if (!clonedItem.ValueOptions) {
        clonedItem.PresetValue = multilineValueInputProcessor(customerSelectedProductCondition.Attribute, permissions.IsEnhancedPAFilterP1Enabled);
      } else {
        const valueOptions = clonedItem.ValueOptions;

        const findHelper = (valueOption, valueB) => {
          if (valueOption.Value.toUpperCase() === valueB.toUpperCase()) {
            return true;
          } else if (valueOption.Text.toUpperCase() === valueB.toUpperCase()) {
            return true;
          }
          return false;
        };
        // New feature, We can now select multiple values.
        const selectedValueArray = customerSelectedProductCondition.Attribute.split(';;');
        // Keep this for single-select just in case.
        const selectedValueOption = find(valueOptions, valueOption => findHelper(valueOption, selectedValueArray[0]));
        const selectedValueOptions = [];

        selectedValueArray.forEach((e) => {
          selectedValueOptions.push(find(valueOptions, valueOption => findHelper(valueOption, e)));
        });

        // If this is migrated from legacy, we will need to uncapitalize the valueoptions from all-caps.
        if (!isDynamicDataFeedBasedCampaign) {
          valueOptions.forEach((valueOption) => {
            valueOption.Value = valueOption.Text;
          });
        }

        // if this is a valueoption that is selected.
        if (selectedValueOption) {
          selectedValueOption.Selected = true;
          // eslint-disable-next-line no-return-assign
          selectedValueOptions.forEach(e => e.Selected = true);
        } /* else if (this.activityLogger) {
          // this should never happen
          this.activityLogger.error(`can not find user selected value option item which is ${JSON.stringify(customerSelectedProductCondition)}`);
        } */
      }

      defaultSelectedDataSource.push(clonedItem);
    } /* else if (this.activityLogger) {
      // this should never happen
      this.activityLogger.error(`can not find user selected production condition item which is ${JSON.stringify(customerSelectedProductCondition)}`);
    } */
  });

  return defaultSelectedDataSource;
};

const getProdutFilters = (providerId, feedId, productData, feedData, permissions, isDynamicDataFeedBasedCampaign) => {
  const customerSelections = get(productData, ['CampaignCriterionsData', 'Criterion', 'ProductConditions'], []);
  const dynamicDataFeedConditionOptions = get(productData, ['DynamicDataFeedConditionOptions'], {});
  const productFilters = [];

  if (!isEmpty(customerSelections)) {
    let selectedConditionOptions = productData.ProductConditionOptions;

    if (isDynamicDataFeedBasedCampaign) {
      const selectedFeedId = feedId || providerId;
      const selectedFeed = find(feedData, item => item.Id === selectedFeedId.toString());
      if (!selectedFeed) {
        console.error(`can not find Feed data, feedId is ${feedId}`);
        // this.activityLogger.error(`can not find Feed data, feedId is ${feedId}`);
      }
      const selectedCategory = selectedFeed.FeedType;
      selectedConditionOptions = dynamicDataFeedConditionOptions[selectedCategory];
    }

    const selectedItems = defaultSelectedDataSourceHelper(selectedConditionOptions, customerSelections, permissions, isDynamicDataFeedBasedCampaign);

    selectedItems.forEach((item) => {
      const productFilter = composeProductFilter(item);
      productFilters.push(productFilter);
    });
  }

  return productFilters;
};

export const modelToViewModelForCampaign = ({
  campaignModel,
  dayTimeCriterionsModel = [],
  locationCriterionsModel,
  bundleCountries,
  targetCriterionsModel,
  audienceCriterionsModel = [],
  productData,
  feedData,
  permissions,
}) => {
  let data = {
    ...pick(campaignModel, [...whitelistedSchemaPropertyKeysForModel, 'ImportProperties']),
  };

  data = extend(data, modelToViewModelForConversionGoals(campaignModel));

  if (dayTimeCriterionsModel) {
    data = extend(data, modelToViewModelForDayTimeTargets({ model: dayTimeCriterionsModel }));
  }

  if (!isEmpty(locationCriterionsModel)) {
    data = extend(data, modelToViewModelForLocationTargets({ model: locationCriterionsModel, bundleCountries, campaignId: campaignModel.Id }));
  }

  if (!isEmpty(targetCriterionsModel)) {
    data = extend(data, modelToViewModelForTargetCriterions({ model: targetCriterionsModel, level: CRITERION_LEVEL.CAMPAIGN }));
  }

  data = extend(data, modelToViewModelForCampaignLevelAudienceCriterions({ model: audienceCriterionsModel }));

  if (campaignModel.CampaignSettings && campaignModel.CampaignSettings[0] && campaignModel.CampaignSettings[0]['@odata.type'] === '#Model.ShoppingSettings') {
    data.ProviderId = campaignModel.CampaignSettings[0].ProviderId;
    data.FeedId = campaignModel.CampaignSettings[0].FeedId;
    data.SalesCountry = campaignModel.CampaignSettings[0].SalesCountry;
    data.FeedLabel = campaignModel.CampaignSettings[0].FeedLabel;
    data.LocalInventoryAdsEnabled = campaignModel.CampaignSettings[0].LocalInventoryAdsEnabled;
    data.ShoppableAdsEnabled = campaignModel.CampaignSettings[0].ShoppableAdsEnabled;
    data.Priority = campaignModel.CampaignSettings[0].Priority;
    data.FeedTypeId = campaignModel.CampaignSettings[0].FeedTypeId;
  }

  data.StoreName = getStoreNameById(data.ProviderId, data.FeedId, productData, feedData);
  data.ProductFilters = getProdutFilters(data.ProviderId, data.FeedId, productData, feedData, permissions, campaignModel.CampaignSubType === CampaignSubTypes.AudienceDynamicFeedBased);

  const campaignViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };

  return campaignViewModel;
};
